import * as React from 'react'
import Link from 'next/link'

export type ViocCopy = {
  meta: { title: string; description: string }
  header: {
    subtitle: string
    title: string
    bullets: [string, string, string]
    placeholder: string
    scheduleButton: string
    reviewsLabel: string
  }
  whyInspection: { title: string; description: string; reasons: { title: string; description: string }[] }
  includedWithInspection: {
    title: string
    description: string
    items: { title: string; description: string }[]
  }
  howItWorks: {
    title: string
    steps: { title: string; description: string }[]
    buttonText: string
  }
  reviews: {
    partnerLabel: string
    title: string
    reviewCountLabel: string
    first: { review: string; reviewer: string }
    second: { review: string; reviewer: string }
    third: { review: string; reviewer: string }
  }
  notYourTypical: {
    title: string
    description: string
    items: string[]
    buttonText: string
  }
  faq: { title: string; faqs: { question: string; answer: string | React.ReactElement }[] }
  happiness: { title: string; description: React.ReactElement }
  ctaButton: string
}

const warrantyLink = (href: string, text: string) => (
  <Link href={href}>
    <a target="_blank" rel="noreferrer">
      {text}
    </a>
  </Link>
)

const viocCopyEn: ViocCopy = {
  meta: {
    title: 'NuBrakes Mobile Brake Repair',
    description: 'Drive safely with confidence. Schedule your free brake inspection with NuBrakes.',
  },
  header: {
    subtitle: 'Preferred Brake Partner',
    title: 'SCHEDULE YOUR FREE BRAKE INSPECTION',
    bullets: [
      'We come to you — or meet at Valvoline',
      'Free Brake Inspection ($79 Value)',
      'Completed in 20 minutes',
    ],
    placeholder: 'Enter your Phone Number here',
    scheduleButton: 'SCHEDULE INSPECTION',
    reviewsLabel: '(14,270 reviews)',
  },
  whyInspection: {
    title: 'WHY YOU SHOULD GET A BRAKE INSPECTION',
    description:
      'Getting your brakes inspected is essential — we make it easy at home, work, or a Valvoline location.',
    reasons: [
      {
        title: 'SAFETY',
        description: 'Protect yourself and your loved ones. Faulty brakes risk accidents and lives.',
      },
      {
        title: 'COST SAVINGS',
        description: 'Spot early wear with regular checks, saving costly future repairs.',
      },
      {
        title: 'PERFORMANCE',
        description: 'Good brakes mean better handling, even in emergencies.',
      },
      {
        title: 'PEACE OF MIND',
        description: "Drive confidently, knowing your brakes won't let you down.",
      },
    ],
  },
  includedWithInspection: {
    title: ' Included With Your Free (20-Minute) Brake Inspection — At Home or On-Site',
    description: "We'll come to you, or you can meet us at a Valvoline location by appointment.",
    items: [
      { title: 'FREE ($79 Value)', description: 'Brake Inspection' },
      { title: 'UP TO $50 OFF', description: 'Any Future Repair' },
      { title: '20 MINS', description: 'To Complete Inspection' },
      { title: 'DIGITAL INSPECTION', description: 'Detailed Brake Report' },
      { title: '100%', description: 'Satisfaction Guarantee' },
      { title: '2 YEAR / 24,000', description: 'MILE Warranty' },
    ],
  },
  howItWorks: {
    title: 'How It Works – In Person or At Your Door',
    steps: [
      {
        title: 'FREE INSPECTION — AT YOUR HOME OR OUR PARTNER LOCATION',
        description:
          "We'll inspect your brakes at your convenience and provide a no-obligation repair timeline and quote.",
      },
      {
        title: 'CHOOSE YOUR PREFERRED REPAIR LOCATION',
        description:
          "Need service? We'll come to you or meet you again at one of our partnered locations — 7 days a week.",
      },
      {
        title: 'GET BACK ON THE ROAD WITH CONFIDENCE',
        description:
          'Same great service, wherever you are. No waiting rooms. No pressure. Just safe, reliable brake repair.',
      },
    ],
    buttonText: 'GET A FREE INSPECTION & QUOTE',
  },
  reviews: {
    partnerLabel: 'PREFERRED BRAKE PARTNER',
    title: 'WHY CUSTOMERS LOVE NUBRAKES',
    reviewCountLabel: '(14,270 reviews)',
    first: {
      review:
        'We received fantastic, quick service, at a fair price. We got quite a few quotes and NuBrakes was the best price and it turns out, the quickest and best service. Our technician, Mike, was knowledgeable and friendly and got the job done quickly - all at my office. We would definitely recommend them and use the service again.',
      reviewer: 'Tami F.',
    },
    second: {
      reviewer: 'Clinton B.',
      review:
        "This service is awesome. You are paying for fast, convenient and good quality work. Communication with Walker was immediate and the technician Micheal was done in half the time that was quoted. I will use NuBrakes again in the future and recommend it to everyone. I mean, they come to you, that was what sold it for me.",
    },
    third: {
      review:
        "Without a doubt would recommend NuBrakes to anyone that needs brakes done. Service tech showed up on time and was professional and quick. And the conveniences having them come to you is hard to beat. Not to mention the price is close to half what you'd pay at a pep boys or any other body shop. Can't beat it.",
      reviewer: 'William T.',
    },
  },
  notYourTypical: {
    title: 'Not Your Typical Service Provider — Local & Convenient',
    description:
      'We offer brake service at home or at select Valvoline locations (by appointment only). High-quality parts, no upsells — just what you need, where you need it',
    items: [
      'Free inspection, completed in 20 minutes',
      'Let us come to you or meet us at a Valvoline location',
      'Premium parts backed by 2-year / 24,000-mile warranty',
      'Only recommending repairs you actually need',
    ],
    buttonText: 'GET A FREE INSPECTION & QUOTE',
  },
  faq: {
    title: 'HAVE A QUESTION? GET IT ANSWERED',
    faqs: [],
  },
  happiness: { title: '', description: <></> },
  ctaButton: 'SCHEDULE FREE INSPECTION',
}

const faqsEn = [
  {
    question: 'IS THE INSPECTION REALLY FREE?',
    answer: (
      <div>
        <p>
          Yes! Whether you meet us at home or at one of our partner locations, the inspection is always 100% free and
          comes with no obligation.
        </p>
      </div>
    ),
  },
  {
    question: 'DO I HAVE TO GET MY BRAKES REPAIRED WHEN YOU COME OUT?',
    answer: (
      <div>
        <p>
          Not at all. You&apos;ll receive a full inspection and a no-obligation quote. If service is needed, you can choose
          to book a mobile repair or return to our partner location by appointment.
        </p>
      </div>
    ),
  },
  {
    question: 'DOES NUBRAKES OFFER A WARRANTY?',
    answer: (
      <div>
        <p>
          Yes, all of our repairs are backed by our {warrantyLink('/warranty', '24 month, 24,000 mile warranty')}.
        </p>
        <p>
          We&apos;re committed to providing a transparent, affordable and ultra-convenient brake repair experience, and we
          stand behind our work.
        </p>
      </div>
    ),
  },
  {
    question: 'HOW LONG DOES IT TAKE?',
    answer: (
      <div>
        <p>Your brake inspection will take about 20 minutes to complete.</p>
        <p>A brake repair service will take about 60 minutes to complete.</p>
      </div>
    ),
  },
  {
    question: "WHO ARE NUBRAKES' TECHNICIANS?",
    answer: (
      <div>
        <p>
          Our full-time technicians are experienced professionals with deep expertise in brake systems. They perform
          inspections and repairs at homes, workplaces, or partner locations, depending on customer preference.
        </p>
      </div>
    ),
  },
  {
    question: 'WHAT TYPE OF PARTS DOES NUBRAKES USE?',
    answer: (
      <div>
        <p>High-quality parts are of the utmost importance when repairing your brakes.</p>
        <p>
          At NuBrakes, most frequently we use professional ceramic or semi-metallic brake pads that are equivalent or
          better than OEM-recommended parts.
        </p>
        <p>
          These parts offer exceptional performance and are backed by our{' '}
          {warrantyLink('/warranty', '24 month, 24,000 mile warranty')}.
        </p>
      </div>
    ),
  },
  {
    question: 'Can I Visit NuBrakes in Person?',
    answer: (
      <div>
        <p>
          We have trained NuBrakes representatives based inside select Valvoline Instant Oil Change locations where you
          can schedule an inspection or consultation. You&apos;ll see NuBrakes signage on the building and our entrance — or
          you can book a mobile visit online at a location of your choice.
        </p>
      </div>
    ),
  },
]

const faqsEs = [
  {
    question: '¿LA INSPECCIÓN ES REALMENTE GRATIS?',
    answer: (
      <div>
        <p>
          ¡Sí! Ya sea en tu casa o en una de nuestras ubicaciones asociadas, la inspección siempre es 100% gratis y sin
          compromiso.
        </p>
      </div>
    ),
  },
  {
    question: '¿TENGO QUE REPARAR LOS FRENOS CUANDO VENGAN?',
    answer: (
      <div>
        <p>
          Para nada. Recibirás una inspección completa y un presupuesto sin compromiso. Si necesitas servicio, puedes
          agendar una reparación móvil o volver a nuestra ubicación asociada con cita.
        </p>
      </div>
    ),
  },
  {
    question: '¿NUBRAKES OFRECE GARANTÍA?',
    answer: (
      <div>
        <p>
          Sí, todas nuestras reparaciones tienen la {warrantyLink('/warranty', 'garantía de 24 meses o 24,000 millas')}.
        </p>
        <p>
          Estamos comprometidos a ofrecer una experiencia de reparación de frenos transparente, económica y muy
          conveniente, y respaldamos nuestro trabajo.
        </p>
      </div>
    ),
  },
  {
    question: '¿CUÁNTO TARDA?',
    answer: (
      <div>
        <p>La inspección de frenos tarda aproximadamente 20 minutos.</p>
        <p>El servicio de reparación de frenos tarda aproximadamente 60 minutos.</p>
      </div>
    ),
  },
  {
    question: '¿QUIÉNES SON LOS TÉCNICOS DE NUBRAKES?',
    answer: (
      <div>
        <p>
          Nuestros técnicos de tiempo completo son profesionales con amplia experiencia en sistemas de frenos. Realizan
          inspecciones y reparaciones en hogares, lugares de trabajo o ubicaciones asociadas, según la preferencia del
          cliente.
        </p>
      </div>
    ),
  },
  {
    question: '¿QUÉ TIPO DE PIEZAS USA NUBRAKES?',
    answer: (
      <div>
        <p>Las piezas de alta calidad son fundamentales al reparar tus frenos.</p>
        <p>
          En NuBrakes usamos con frecuencia pastillas de freno cerámicas o semi-metálicas profesionales equivalentes o
          superiores a las piezas recomendadas por el fabricante.
        </p>
        <p>
          Estas piezas ofrecen un rendimiento excepcional y están respaldadas por nuestra{' '}
          {warrantyLink('/warranty', 'garantía de 24 meses o 24,000 millas')}.
        </p>
      </div>
    ),
  },
  {
    question: '¿Puedo visitar NuBrakes en persona?',
    answer: (
      <div>
        <p>
          Tenemos representantes de NuBrakes en selectas ubicaciones de Valvoline Instant Oil Change donde puedes
          agendar una inspección o consulta. Verás la señalización de NuBrakes en el edificio y en nuestra entrada, o
          puedes reservar una visita móvil en línea en la ubicación de tu preferencia.
        </p>
      </div>
    ),
  },
]

viocCopyEn.faq = { title: 'HAVE A QUESTION? GET IT ANSWERED', faqs: faqsEn }
viocCopyEn.happiness = {
  title: 'Happiness Guaranteed',
  description: (
    <>
      Our brake techs have decades of combined experience, and all services are backed by our{' '}
      <Link href="/warranty">NuBrakes Guarantee</Link>.
    </>
  ),
}

export { viocCopyEn }

export const viocCopyEs: ViocCopy = {
  ...viocCopyEn,
  meta: {
    title: 'NuBrakes Reparación de Frenos Móvil',
    description: 'Conduce con seguridad y confianza. Agenda tu inspección de frenos gratis con NuBrakes.',
  },
  header: {
    subtitle: 'Socio Preferido de Frenos',
    title: 'AGENDA TU INSPECCIÓN DE FRENOS GRATIS',
    bullets: [
      'Vamos a ti — o nos vemos en Valvoline',
      'Inspección de frenos gratis (valor $79)',
      'Hecha en 20 minutos',
    ],
    placeholder: 'Ingresa tu número de teléfono',
    scheduleButton: 'AGENDAR INSPECCIÓN',
    reviewsLabel: '(14,270 reseñas)',
  },
  whyInspection: {
    title: 'POR QUÉ DEBERÍAS HACERTE UNA INSPECCIÓN DE FRENOS',
    description:
      'Inspeccionar tus frenos es esencial; nosotros lo hacemos fácil en casa, en el trabajo o en una ubicación Valvoline.',
    reasons: [
      {
        title: 'SEGURIDAD',
        description: 'Protege tu vida y la de los tuyos. Los frenos en mal estado ponen en riesgo accidentes y vidas.',
      },
      {
        title: 'AHORRO',
        description: 'Detecta el desgaste a tiempo con revisiones regulares y evita reparaciones costosas.',
      },
      {
        title: 'RENDIMIENTO',
        description: 'Unos buenos frenos significan mejor control, incluso en emergencias.',
      },
      {
        title: 'TRANQUILIDAD',
        description: 'Conduce con confianza sabiendo que tus frenos no te fallarán.',
      },
    ],
  },
  includedWithInspection: {
    title: ' Incluido con tu inspección de frenos gratis',
    description: 'Vamos a ti o puedes ir a una ubicación Valvoline (con cita previa).',
    items: [
      { title: 'GRATIS (valor $79)', description: 'Inspección de frenos' },
      { title: 'HASTA $50 DE DESCUENTO', description: 'En cualquier reparación futura' },
      { title: '20 MIN', description: 'Para completar la inspección' },
      { title: 'INSPECCIÓN DIGITAL', description: 'Reporte detallado de frenos' },
      { title: '100%', description: 'Garantía de satisfacción' },
      { title: '2 AÑOS / 24,000', description: 'MILLAS de garantía' },
    ],
  },
  howItWorks: {
    title: 'Cómo funciona — en persona o en tu casa',
    steps: [
      {
        title: 'INSPECCIÓN GRATIS — EN TU CASA O UBICACIÓN ASOCIADA',
        description:
          'Inspeccionamos tus frenos cuando te convenga y te damos un plazo y presupuesto de reparación sin compromiso.',
      },
      {
        title: 'ELIGE TU UBICACIÓN PREFERIDA PARA LA REPARACIÓN',
        description:
          '¿Necesitas servicio? Vamos a ti o te esperamos en una de nuestras ubicaciones asociadas — 7 días a la semana.',
      },
      {
        title: 'VUELVE A LA CARRETERA CON CONFIANZA',
        description:
          'El mismo gran servicio, donde sea que estés. Sin salas de espera. Sin presión. Solo reparación de frenos segura y confiable.',
      },
    ],
    buttonText: 'OBTENER INSPECCIÓN Y PRESUPUESTO GRATIS',
  },
  reviews: {
    partnerLabel: 'SOCIO PREFERIDO DE FRENOS',
    title: 'POR QUÉ LOS CLIENTES AMAN NUBRAKES',
    reviewCountLabel: '(14,270 reseñas)',
    first: {
      review:
        'Recibimos un servicio fantástico y rápido a un precio justo. Obtuvimos varias cotizaciones y NuBrakes fue el mejor precio y resultó ser el servicio más rápido y mejor. Nuestro técnico, Mike, fue conocedor y amable e hizo el trabajo rápidamente, todo en mi oficina. Sin duda los recomendaríamos y usaríamos el servicio de nuevo.',
      reviewer: 'Tami F.',
    },
    second: {
      reviewer: 'Clinton B.',
      review:
        'Este servicio es increíble. Pagas por trabajo rápido, conveniente y de buena calidad. La comunicación con Walker fue inmediata y el técnico Michael terminó en la mitad del tiempo cotizado. Usaré NuBrakes de nuevo en el futuro y lo recomiendo a todos. Vienen a ti, eso fue lo que me convenció.',
    },
    third: {
      review:
        'Sin duda recomendaría NuBrakes a cualquiera que necesite frenos. El técnico llegó a tiempo, fue profesional y rápido. Y la comodidad de que vengan a ti no tiene precio. Por no mencionar que el precio es casi la mitad de lo que pagarías en Pep Boys u otro taller. Inigualable.',
      reviewer: 'William T.',
    },
  },
  notYourTypical: {
    title: 'No somos un proveedor cualquiera — local y conveniente',
    description:
      'Ofrecemos servicio de frenos en casa o en ubicaciones selectas de Valvoline (solo con cita). Piezas de alta calidad, sin ventas adicionales — solo lo que necesitas, donde lo necesitas.',
    items: [
      'Inspección gratis, completada en 20 minutos',
      'Vamos a ti o nos vemos en una ubicación Valvoline',
      'Piezas premium con garantía de 2 años / 24,000 millas',
      'Solo recomendamos las reparaciones que realmente necesitas',
    ],
    buttonText: 'OBTENER INSPECCIÓN Y PRESUPUESTO GRATIS',
  },
  faq: { title: '¿TIENES PREGUNTAS? TE LAS RESPONDEMOS', faqs: faqsEs },
  happiness: {
    title: 'Garantía de satisfacción',
    description: (
      <>
        Nuestros técnicos tienen décadas de experiencia combinada y todos los servicios están respaldados por nuestra{' '}
        <Link href="/warranty">Garantía NuBrakes</Link>.
      </>
    ),
  },
  ctaButton: 'AGENDAR INSPECCIÓN GRATIS',
}

export function getViocCopy(locale: 'en' | 'es'): ViocCopy {
  return locale === 'es' ? viocCopyEs : viocCopyEn
}
